import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { JsonLd } from "~/components/seo/JsonLd";
import {
  type SkillsPageData,
  type RelatedBySkills,
} from "~/lib/skills-seo-queries";
import { slugify, generateHreflangLinks } from "~/lib/seo-utils";
import { SkillsList, SkillsCTA } from "~/components/seo/SkillsCard";
import { SalaryBreadcrumb } from "~/components/seo/SalaryCard";
import { InternalLinks } from "~/components/seo/InternalLinks";

const fetchSkillsForOccupation = createServerFn({ method: "GET" })
  .validator((value: string) => value)
  .handler(
    async ({ data: occupationSlug }): Promise<SkillsPageData & { related: RelatedBySkills[] }> => {
      const { getSkillsForOccupation, getRelatedOccupationsBySkillOverlap } = await import("~/lib/skills-seo-queries");
      const skillsData = await getSkillsForOccupation(occupationSlug);
      if (!skillsData) throw notFound();
      const related = await getRelatedOccupationsBySkillOverlap(skillsData.occupation.uri);
      return { ...skillsData, related };
    },
  );

export const Route = createFileRoute("/skills/$occupation")({
  loader: ({ params }) => fetchSkillsForOccupation({ data: params.occupation }),
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const name = loaderData.occupation.labelDe;
    const topSkills = loaderData.essentialSkills.slice(0, 3).map((s) => s.labelDe).join(", ");
    const slug = slugify(name);
    const description = `${name}: ${loaderData.essentialSkills.length} essentielle Skills inkl. ${topSkills}. Vergleiche dein Profil mit den Anforderungen.`;
    return {
      meta: [
        { title: `Skills für ${name}: Was Arbeitgeber 2026 suchen | Alchema` },
        { name: "description", content: description },
        { property: "og:title", content: `Skills für ${name}: Was Arbeitgeber 2026 suchen | Alchema` },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: `https://alchema.eu/skills/${slug}` },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: `Skills für ${name} | Alchema` },
        { name: "twitter:description", content: description },
        { name: "robots", content: "index, follow" },
      ],
      links: generateHreflangLinks(`/skills/${slug}`),
    };
  },
  component: SkillsOccupationPage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-4xl px-6 py-20 text-center">
      <h1 className="text-2xl font-bold" style={{ color: "var(--text)" }}>
        Beruf nicht gefunden
      </h1>
      <p className="mt-3 text-sm" style={{ color: "var(--text-secondary)" }}>
        Für diesen Beruf liegen keine Skills-Daten vor.
      </p>
      <Link
        to="/skills"
        className="mt-6 inline-flex rounded-[10px] px-5 py-2.5 text-sm font-medium text-white"
        style={{ background: "var(--accent)" }}
      >
        Alle Berufe ansehen
      </Link>
    </div>
  ),
});

function SkillsOccupationPage() {
  const { occupation, essentialSkills, optionalSkills, related } = Route.useLoaderData();
  const occSlug = slugify(occupation.labelDe);

  // Schema.org JSON-LD
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Occupation",
    name: occupation.labelDe,
    alternateName: occupation.labelEn,
    description: occupation.descriptionDe ?? `Skills und Anforderungen für ${occupation.labelDe}`,
    skills: essentialSkills.map((s: { labelDe: string; uri: string }) => ({
      "@type": "DefinedTerm",
      name: s.labelDe,
      url: s.uri,
    })),
  };

  return (
    <div style={{ backgroundColor: "var(--bg)" }}>
      <JsonLd json={JSON.stringify(jsonLd)} />
      <section className="mx-auto max-w-4xl px-6 pb-24 pt-20">
        <SalaryBreadcrumb
          items={[
            { label: "Skills", href: "/skills" },
            { label: occupation.labelDe },
          ]}
        />

        <h1 className="mt-6 text-3xl font-bold tracking-tight" style={{ color: "var(--text)" }}>
          Skills für {occupation.labelDe}
        </h1>
        <p className="mt-2 text-base" style={{ color: "var(--text-secondary)" }}>
          Was Arbeitgeber 2026 wirklich suchen — basierend auf dem EU ESCO-Framework.
        </p>

        {occupation.descriptionDe && (
          <p className="mt-3 max-w-2xl text-sm leading-relaxed" style={{ color: "var(--text-muted)" }}>
            {occupation.descriptionDe}
          </p>
        )}

        {/* Essential skills */}
        <div className="mt-8">
          <SkillsList title="Essentielle Skills" skills={essentialSkills} variant="essential" />
        </div>

        {/* Optional skills */}
        {optionalSkills.length > 0 && (
          <div className="mt-8">
            <SkillsList title="Wünschenswerte Skills" skills={optionalSkills} variant="optional" />
          </div>
        )}

        {/* Link to salary page */}
        <div className="mt-8">
          <Link
            to="/gehalt/$beruf"
            params={{ beruf: occSlug }}
            className="text-sm font-medium hover:underline"
            style={{ color: "var(--accent)" }}
          >
            Gehalt als {occupation.labelDe} ansehen →
          </Link>
        </div>

        {/* CTA */}
        <div className="mt-10">
          <SkillsCTA />
        </div>

        {/* Related occupations by skill overlap */}
        {related.length > 0 && (
          <div className="mt-10">
            <h2 className="mb-4 text-lg font-semibold" style={{ color: "var(--text)" }}>
              Ähnliche Berufe (nach Skill-Überschneidung)
            </h2>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {related.map((r: RelatedBySkills) => (
                <Link
                  key={r.slug}
                  to="/skills/$occupation"
                  params={{ occupation: r.slug }}
                  className="flex items-center justify-between rounded-xl border px-4 py-3 transition hover:shadow-sm"
                  style={{ borderColor: "var(--border)", background: "var(--surface)" }}
                >
                  <span className="text-sm font-medium" style={{ color: "var(--text)" }}>
                    {r.occupationName}
                  </span>
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                    {r.sharedSkills} gemeinsame Skills
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10">
          <InternalLinks
            currentType="skills"
            occupationSlug={occSlug}
            occupationLabel={occupation.labelDe}
            relatedSlugs={related.map((r: RelatedBySkills) => ({
              slug: r.slug,
              label: `Skills für ${r.occupationName}`,
            }))}
          />
        </div>

        {/* Source */}
        <p className="mt-10 text-xs" style={{ color: "var(--text-muted)" }}>
          Quelle: ESCO (European Skills, Competences, Qualifications and Occupations) — Europäische Kommission.
        </p>
      </section>
    </div>
  );
}
